import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Break out and haul away existing asphalt driveway, 600 sq ft', amount: '$1,500' },
  { item: 'Excavate, 4 in compacted gravel base, forms, #3 rebar on 24 in grid', amount: '$1,800' },
  { item: 'Pour 4 in slab, 4,000 psi mix, broom finish, saw-cut control joints', amount: '$4,200' },
  { item: 'Optional: penetrating sealer after a 28-day cure', amount: '$480' },
];

const faq = [
  {
    q: 'Will my new concrete crack?',
    a: 'Concrete can crack even when it is poured well, which is why control joints are cut to guide where it happens. Put a plain statement about cracking in your terms, and say what your workmanship promise covers, so the homeowner reads it before the pour rather than after the first hairline appears.',
  },
  {
    q: 'When can we drive on a new driveway?',
    a: 'Most contractors tell customers to keep foot traffic off for a day or two and cars off for about a week, longer in cold weather. Whatever your rule is, write it on the quote, because it is the question every homeowner asks.',
  },
  {
    q: 'Does Scopie calculate cubic yards or do takeoffs from plans?',
    a: 'No. Scopie does not measure from drawings or work out yardage. It is meant for residential flatwork you measure on site: the quote, the signature, the pour date and the invoice.',
  },
  {
    q: 'Can I visit the site before I send a price?',
    a: 'Yes. Log the request and book an assessment visit on your schedule. After you have measured and checked the grade, build the quote for that request and send the link.',
  },
];

const page: IndustryPage = {
  slug: 'concrete',
  order: 11,
  group: 'Outdoor',
  path: '/for/concrete',
  primaryKeyword: 'concrete contractor software',
  secondaryKeywords: ['concrete estimate template', 'concrete driveway quote', 'concrete contractor deposit'],
  title: 'Concrete Contractor Software for Flatwork Quotes | Scopie', // 56
  metaDescription:
    'Driveways, patios and walks quoted with tear-out, base, rebar, finish and sealing on separate lines, plus your crack and cure terms, signed before pouring.',
  h1: 'Flatwork quotes for driveways, patios and walks',
  answerFirst:
    'Scopie is built for residential concrete crews who quote from a site visit: tear-out, base, reinforcement, the pour and the finish each get a line, and sealing can be an optional extra. Your cracking, curing and weather terms ride along on every quote, and the homeowner signs before you order the truck.',
  exampleQuote: { label: 'Sample driveway replacement quote', lines, total: '$7,980', depositNote: 'Pour-day cancellation and deposit wording are the contractor\'s own clauses.' },
  blocks: [
    {
      type: 'text',
      id: 'under',
      h2: 'Homeowners see the surface. Your price is mostly underneath.',
      lead: 'The other guy was two thousand cheaper. He was also pouring straight onto the old dirt.',
      paragraphs: [
        'A finished slab looks the same on day one whether it sits on four inches of compacted gravel or on nothing. The difference shows up three winters later. Listing demolition, base, forms and steel as separate lines lets the homeowner see what they are paying for and makes the bid that skipped them easy to spot.',
        'Finishes belong on their own lines as well. Broom, exposed aggregate and stamped patterns are different jobs, and a homeowner choosing between them needs to see the cost of each.',
      ],
    },
    {
      type: 'list',
      id: 'site-visit',
      h2: 'What to check on the site visit, and write down',
      style: 'checklist',
      items: [
        { text: 'Square footage and thickness, plus any thickened edges or apron.' },
        { text: 'Grade and drainage: which way water runs now, and which way it should.' },
        { text: 'Access for the truck, or whether you need a pump or buggies.' },
        { text: 'Utilities and sprinkler lines, and who calls in the locate.' },
        { text: 'Permit needs at the street cut or apron.' },
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'Replacing a 600 sq ft driveway',
      label: 'Sample quote: asphalt out, concrete in',
      lines,
      total: '$7,980',
      totalLabel: 'Total with sealing chosen',
      note: 'Example figures only. Sealing is offered as an optional line the homeowner can tick.',
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'What costs concrete contractors on paper',
      style: 'mistakes',
      items: [
        { title: 'No deposit before the pour', text: 'Ready-mix, a pump and a finishing crew are booked for one morning. Your deposit rule should be signed before they are.' },
        { title: 'Cracking not addressed', text: 'Silence reads like a guarantee. Say what is normal and what you stand behind.' },
        { title: 'Weather left vague', text: 'Rain and frost move pours. Write that the date moves and the price does not.' },
        { title: 'Layout changes on pour day', text: 'A wider apron is a new price. Say how changes are handled.' },
        { title: 'Old quotes honored at old prices', text: 'Material costs move. Give each quote a validity date.' },
      ],
    },
    {
      type: 'text',
      id: 'season',
      h2: 'Pour season and the rush to beat the frost',
      paragraphs: [
        'In most of the country, residential flatwork runs from spring thaw to the first hard frost, and fall fills up fast with people who want the driveway done before winter. Quotes that are signed early get the dates. Once signed, the pour goes on the schedule with the crew and the skid steer or forms trailer assigned, and the homeowner gets the invoice as a link when the forms come off.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Concrete quote questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/landscaping', anchor: 'Scopie for landscaping businesses', status: 'live' },
    { href: '/for/fencing', anchor: 'Scopie for fence contractors', status: 'live' },
    { href: '/templates/concrete-estimate-template', anchor: 'free concrete estimate template', status: 'live' },
  ],
  cta: {
    heading: 'Get the fall pours signed early',
    text: 'Store your base, steel and finish items in Scopie and quote each driveway from the curb.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
